import { fetchAuthSession } from 'aws-amplify/auth';
import { BACKEND_AGENTS_API } from '../env';

/**
 * Busca los resultados de consulta persistidos por el executor de un agente
 * (Athena, etc.) y, si existen, la configuración de gráfica ya generada.
 *
 * Reemplaza las llamadas directas del navegador a DynamoDB (Query) y Bedrock
 * (InvokeModel) que antes hacía Chat.js con credenciales de un Cognito
 * Identity Pool — ahora todo pasa por una Lambda de whisperbi-agent-management.
 *
 * @param {{queryUuid: string, answerText: string, invocationId?: string}} params
 * @returns {Promise<{queryResults: Array, chart: object|null}>}
 */
const ChartDataAPI = {
  // invocationId: el turno al que pertenece la gráfica; con él, chart-data
  // suma lo que cobra a la auditoría de esa pregunta (igual que por WebSocket).
  async getChartData({ queryUuid, answerText, invocationId }) {
    if (!queryUuid || !answerText) {
      return { queryResults: [], chart: null };
    }

    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();

    const response = await fetch(`${BACKEND_AGENTS_API}/agent/chart-data`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`
      },
      body: JSON.stringify({
        query_uuid: queryUuid,
        answer_text: answerText,
        ...(invocationId ? { invocation_id: invocationId } : {})
      })
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => ({}));
      const error = new Error(errorBody.error || `HTTP ${response.status}: no se pudo obtener la información de la gráfica`);
      error.status = response.status;
      error.code = errorBody.error;
      // Motivo de credits-engine en un 402 (USER_CREDIT_LIMIT_EXCEEDED, ...).
      error.reason = errorBody.reason;
      throw error;
    }

    return response.json();
  }
};

/**
 * Texto para el usuario cuando chart-data falla (se muestra debajo de la
 * respuesta, en vez de que la gráfica simplemente no aparezca).
 */
// Un 402 no siempre es falta de saldo: con saldo de sobra, el límite diario
// o mensual de créditos también rechaza la reserva.
const DENY_REASON_MESSAGES = {
  USER_CREDIT_LIMIT_EXCEEDED: 'No se generó la gráfica: alcanzaste tu límite de créditos (diario o mensual).',
  ACCOUNT_CREDIT_LIMIT_EXCEEDED: 'No se generó la gráfica: la cuenta alcanzó su límite de créditos (diario o mensual).',
  OPERATION_LIMIT_EXCEEDED: 'No se generó la gráfica: supera el máximo de créditos permitido por operación.',
  PLAN_REQUIRED: 'No se generó la gráfica: requiere un plan de pago.',
  MODEL_PLAN_REQUIRED: 'No se generó la gráfica: su modelo requiere un plan de pago.',
  ACCOUNT_INACTIVE: 'No se generó la gráfica: la cuenta está inactiva.',
  PAYMENT_PAST_DUE: 'No se generó la gráfica: hay un pago pendiente en la cuenta.'
};

export const chartErrorMessage = (error) => {
  if (error?.status === 402) {
    return DENY_REASON_MESSAGES[error.reason] || 'No se generó la gráfica: no hay créditos suficientes.';
  }
  if (error?.status === 429) {
    return 'No se generó la gráfica: se alcanzó el límite de uso por minuto. Inténtalo de nuevo en unos momentos.';
  }
  return 'No se pudo generar la gráfica de esta respuesta.';
};

export default ChartDataAPI;
